import React, { useCallback, useRef, useState } from 'react';
import { Column } from 'primereact/column';
import Table from '../../../components/Table/Table';
import { formatCurrency, formatDate } from '../../../../utils/formatUtils';
import {
  DataTableRowClickEventParams,
  DataTableRowMouseEventParams,
} from 'primereact/datatable';
import { animateCellText } from '../../../../utils/htmlUtils';
import { PolicySummary } from '../../../Interfaces/Insurance/Policy';
import PolicyDrilldown from './PolicyDrilldown';
import TooltipContent from '../../../components/messages/TooltipContent';
import PolicyAttachment from './PolicyAttachment';
import { Tooltip } from 'primereact/tooltip';

type PoliciesTableProps = {
  policies: PolicySummary[];
};

const PoliciesTable = React.forwardRef<HTMLDivElement, PoliciesTableProps>(
  ({ policies }, ref) => {
    const [showDrilldown, setShowDrilldown] = useState(false);
    const [selectedPolicy, setSelectedPolicy] = useState<string>();
    const tooltipRef = useRef<Tooltip>(null);

    const calcHeight = (rows: number) => {
      const headerHeight = 32;
      const footerHeight = 54;
      const rowHeight = 31;
      return headerHeight + footerHeight + rows * rowHeight + 3;
    };

    const onRowHover = (e: DataTableRowMouseEventParams) => {
      const cell = e.originalEvent.target as HTMLElement;
      const row = cell.closest('tr') as HTMLElement;
      animateCellText(row);
    };

    const rowClassName = () => {
      return 'cursor-pointer';
    };

    const onRowClick = (e: DataTableRowClickEventParams) => {
      setSelectedPolicy(e.data.id);
      setShowDrilldown(true);
    };
    const rowClickCB = useCallback(onRowClick, []);

    let renewal = 0;
    policies.forEach((policy) => {
      renewal += policy.renewal ?? 0;
    });

    return (
      <div className="pdfDivFitContent">
        {selectedPolicy && (
          <PolicyDrilldown
            visible={showDrilldown}
            setVisible={setShowDrilldown}
            policyID={selectedPolicy}
          />
        )}
        <Table
          id="policy-table"
          ref={ref}
          data={policies}
          className={`mx-3 dashboardOptionShadow`}
          calcHeight={useCallback(calcHeight, [])}
          onRowClick={rowClickCB}
          rowClassName={useCallback(rowClassName, [])}
          onRowMouseEnter={useCallback(onRowHover, [])}
          hideColumns={true}
          afterFetching={() => {
            document
              .querySelectorAll('.expired-warning')
              .forEach((el) =>
                tooltipRef.current?.updateTargetEvents(el as HTMLElement)
              );
          }}
        >
          <Column
            headerClassName={`tableHeader`}
            className="tableCell p-0 printHide"
            footerClassName="tableFooter "
          />
          <Column
            field="policy"
            header="Policy"
            body={(policy) => (
              <div className="scroll-text">{policy.policy}</div>
            )}
            headerClassName={`tableHeader font-normal justify-content-center`}
            style={{ minWidth: '200px', maxWidth: '200px' }}
            className={`text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow`}
            footerClassName="tableFooter"
            sortable
          />
          <Column
            field="limit"
            header="Limit"
            body={(policy) => formatCurrency(policy.limit)}
            style={{ minWidth: '120px' }}
            headerClassName="tableHeader font-normal"
            className="justify-content-center text-standard blackText tableCell"
            footerClassName="tableFooter"
            sortable
          />
          <Column
            field="deductible"
            header="Deductible / Retention"
            style={{ minWidth: '170px' }}
            body={(policy) =>
              policy.deductible ? formatCurrency(policy.deductible) : '-'
            }
            headerClassName="tableHeader font-normal"
            className="justify-content-center text-standard blackText tableCell"
            footer="Total"
            footerClassName="tableFooter block text-right border-top-2 border-transparent mt-4 limitBorder relative"
            sortable
          />
          <Column
            field="renewal"
            header="Premium"
            body={(policy) =>
              policy.renewal ? formatCurrency(policy.renewal) : '-'
            }
            style={{ minWidth: '120px' }}
            headerClassName="tableHeader font-normal justify-content-center"
            className="justify-content-end text-standard blackText tableCell"
            footer={formatCurrency(renewal)}
            footerClassName="tableFooter block text-right border-top-2 mt-4"
            sortable
          />
          <Column
            field="termStart"
            header="Term"
            style={{ minWidth: '200px' }}
            body={(policy) => {
              const termEnd = new Date(policy.termEnd);
              const msUntilExpiration =
                termEnd.getTime() - new Date().getTime();
              const daysUntilExpiration = Math.floor(
                msUntilExpiration / (1000 * 60 * 60 * 24)
              );
              const isExpired = msUntilExpiration < 0;
              const expiresWithin90Days =
                !isExpired && daysUntilExpiration <= 90;

              return (
                <span
                  id={`${policy.id}_term`}
                  className={`${
                    isExpired ? 'font-bold errorText' : ''
                  } flex align-items-center gap-1`}
                >
                  {formatDate(policy.termStart)} - {formatDate(policy.termEnd)}
                  {expiresWithin90Days && (
                    <i
                      className="pi pi-exclamation-triangle text-yellow-600 text-17px mx-auto cursor-pointer ml-4 expired-warning"
                      data-pr-position="right"
                      data-pr-tooltip={`Expires ${
                        daysUntilExpiration > 1
                          ? `within ${daysUntilExpiration} days!`
                          : `tomorrow!`
                      }`}
                    />
                  )}
                </span>
              );
            }}
            headerClassName="tableHeader font-normal justify-content-center"
            className="justify-content-left text-standard blackText tableCell"
            footerClassName="tableFooter"
            sortable
          />
          <Column
            field="insurer"
            header="Insurer"
            body={(policy) => (
              <div className="scroll-text">{policy.insurer}</div>
            )}
            headerClassName={`tableHeader font-normal justify-content-center`}
            style={{ minWidth: '200px', maxWidth: '200px' }}
            className={`text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow`}
            footerClassName="tableFooter"
            sortable
          />
          <Column
            field="number"
            header="Policy Number"
            style={{ minWidth: '200px' }}
            headerClassName="tableHeader font-normal justify-content-center text-center"
            className="justify-content-center text-standard blackText tableCell"
            footerClassName="tableFooter"
            sortable
          />
          <Column
            field="billing"
            header="Billing"
            style={{ minWidth: '100px' }}
            headerClassName="tableHeader font-normal justify-content-center text-center"
            className="justify-content-center text-standard blackText tableCell"
            footerClassName="tableFooter"
            sortable
          />
          <Column
            style={{ minWidth: '40px', maxWidth: '40px' }}
            body={(policy) =>
              policy.attachmentName ? (
                <PolicyAttachment
                  policyID={policy.id}
                  attachmentName={policy.attachmentName ?? ''}
                  showIcon={true}
                />
              ) : (
                <div className="text-red-600 font-bold">
                  <TooltipContent
                    label="!"
                    content={`No attachment file was found!`}
                    id={policy.id.replace(/-/g, '')}
                  />
                </div>
              )
            }
            headerClassName="tableHeader font-normal"
            className="justify-content-center text-standard blackText tableCell printHide"
            footerClassName="tableFooter"
          />
          <Column
            headerClassName={`tableHeader`}
            className="tableCell p-0 printHide"
            footerClassName="tableFooter "
          />
        </Table>
        <Tooltip ref={tooltipRef} target=".expired-warning" />
      </div>
    );
  }
);

const transactionsAreEqual = (
  prevTransactions: Readonly<PoliciesTableProps>,
  nextTransactions: Readonly<PoliciesTableProps>
) => {
  return prevTransactions.policies === nextTransactions.policies;
};

export default React.memo(PoliciesTable, transactionsAreEqual);
